import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select, Switch, Tab, Tabs, TextField,
    ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import {
    AUDIENCES, CONSENT_RULES, DEFAULT_LIMITS, SYSTEM_VARIABLES, WHATSAPP_CATEGORIES,
    WHATSAPP_STATUSES, availableChannels, channelText, dataFields, emailBlocks, emailPreview, enabledChannels, hasEmailBlocks, intOrNull, knownVariables,
    moveChannel, previewOf, validate, whatsappTemplate, withChannel, withChannelText, withDataFields, withEmailBlocks, withEmailBlocksMode,
    withEmailTemplateMode, withField, withWhatsappTemplate
} from '../Services/contentModel';
import { parseContent } from '../Services/catalogModel';

const ORANGE = '#f77513';
const pretty = (value) => JSON.stringify(value ?? {}, null, 2);

const Section = ({ title, children, sx }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px', mb: 2, ...sx }}>
        {title && <Typography variant="subtitle2" sx={{ color: ORANGE, fontWeight: 'bold', mb: 1.5 }}>{title}</Typography>}
        {children}
    </Paper>
);

// ---- pré-visualização --------------------------------------------------------------------------------------------------------

/** Como a mensagem aparece, com os dados de exemplo do tipo. O e-mail ainda não tem: ele sai de um modelo do serviço, não de blocos editáveis. */
const ChannelPreview = ({ content, channel }) => {
    const { title, body } = previewOf(content, channel);
    if (!body && !title) return <Typography variant="caption" color="text.secondary" display="block">Escreva o texto para ver a pré-visualização.</Typography>;

    if (channel === 'whatsapp') {
        return (
            <Box sx={{ backgroundColor: '#e7ddd3', p: 1.5, borderRadius: '12px', maxWidth: 340 }}>
                <Box sx={{ backgroundColor: '#dcf8c6', p: 1.25, borderRadius: '10px', whiteSpace: 'pre-wrap', fontSize: 14 }}>{body}</Box>
            </Box>
        );
    }
    return (
        <Box sx={{ backgroundColor: '#f4f4f5', p: 1.5, borderRadius: '12px', maxWidth: 340, display: 'flex', gap: 1.25 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: ORANGE, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
                {title && <Typography sx={{ fontWeight: 700, fontSize: 14, wordBreak: 'break-word' }}>{title}</Typography>}
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{body}</Typography>
            </Box>
        </Box>
    );
};

// ---- aba Atributos -----------------------------------------------------------------------------------------------------------

const AttributesTab = ({ value, onChange, groups, canEditConsent }) => {
    const lock = !canEditConsent;
    const setNumber = (field) => (e) => {
        const n = intOrNull(e.target.value);
        onChange(withField(value, field, Number.isNaN(n) ? e.target.value : n));
    };
    const numberValue = (field) => (value[field] == null ? '' : value[field]);

    return (
        <>
            {lock && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Público, categoria, consentimento e canais mudam a quem e como a pessoa é avisada: só o perfil de editor de consentimento os altera.
                </Alert>
            )}
            <Section title="Quem recebe e com que consentimento">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <FormControl size="small" disabled={lock}>
                        <InputLabel>Público</InputLabel>
                        <Select label="Público" value={value.audience ?? ''} onChange={(e) => onChange(withField(value, 'audience', e.target.value))}>
                            {AUDIENCES.map((a) => <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField size="small" label="Categoria" disabled={lock} value={value.category ?? ''}
                        helperText="Rótulo livre: essencial, lembrete, resumo, jornada, sugestao..."
                        onChange={(e) => onChange(withField(value, 'category', e.target.value))} />
                    <FormControl size="small" disabled={lock}>
                        <InputLabel>Consentimento</InputLabel>
                        <Select label="Consentimento" value={value.consentRule ?? 'none'} onChange={(e) => onChange(withField(value, 'consentRule', e.target.value))}>
                            {CONSENT_RULES.map((r) => <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box>
                        <FormControlLabel disabled={lock} label="A pessoa pode desligar este aviso"
                            control={<Switch checked={value.canDisable === true} onChange={(e) => onChange(withField(value, 'canDisable', e.target.checked))} />} />
                        <FormControlLabel label="Pode ir a quem ainda não tem cadastro"
                            control={<Switch checked={value.allowsLead === true} onChange={(e) => onChange(withField(value, 'allowsLead', e.target.checked ? true : ''))} />} />
                    </Box>
                </Box>
            </Section>

            <Section title="Frequência">
                <FormControlLabel label="Espera a janela de horário (8h às 20h)"
                    control={<Switch checked={value.respectsWindow === true} onChange={(e) => onChange(withField(value, 'respectsWindow', e.target.checked ? true : ''))} />} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mt: 1.5 }}>
                    <FormControl size="small" disabled={value.canDisable !== true}>
                        <InputLabel shrink>Grupo de limite</InputLabel>
                        <Select label="Grupo de limite" notched displayEmpty value={value.capGroup ?? ''} onChange={(e) => onChange(withField(value, 'capGroup', e.target.value))}>
                            <MenuItem value=""><em>Nenhum</em></MenuItem>
                            {(groups ?? []).map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                            {value.capGroup && !(groups ?? []).includes(value.capGroup) && <MenuItem value={value.capGroup}>{value.capGroup} (não publicado)</MenuItem>}
                        </Select>
                    </FormControl>
                    <TextField size="small" label="Teto por dia" disabled={value.canDisable !== true} value={numberValue('capPerDay')} onChange={setNumber('capPerDay')} />
                    <TextField size="small" label="Teto por semana" disabled={value.canDisable !== true} value={numberValue('capPerWeek')} onChange={setNumber('capPerWeek')} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Aviso que a pessoa não pode desligar (essencial) não tem limite. O grupo divide o teto entre vários tipos; o teto do próprio tipo vale por cima.
                </Typography>
            </Section>
        </>
    );
};

// ---- aba Canais --------------------------------------------------------------------------------------------------------------

const VariableChips = ({ content, onPick }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>Variáveis:</Typography>
        {knownVariables(content).map((name) => (
            <Chip key={name} size="small" variant="outlined" label={`{{${name}}}`} onClick={() => onPick(`{{${name}}}`)}
                color={SYSTEM_VARIABLES.includes(name) ? 'default' : 'primary'} />
        ))}
    </Box>
);

/** Título e corpo de um canal de texto (push, no app, SMS); as variáveis entram ao clicar. */
const TextChannelEditor = ({ value, onChange, channel, limits }) => {
    const { title, body } = channelText(value, channel);
    const hasTitle = channel !== 'sms';
    const target = useRef('body');
    const isPush = channel === 'push';

    const set = (field) => (e) => onChange(withChannelText(value, channel, field, e.target.value));
    const insert = (token) => {
        const field = target.current === 'title' && hasTitle ? 'title' : 'body';
        onChange(withChannelText(value, channel, field, (field === 'title' ? title : body) + token));
    };

    return (
        <>
            {hasTitle && (
                <TextField size="small" fullWidth label="Título" sx={{ mb: 1.5 }} value={title} onChange={set('title')} onFocus={() => { target.current = 'title'; }}
                    error={isPush && title.length > limits.pushTitleMax} helperText={isPush ? `${title.length}/${limits.pushTitleMax}` : undefined} />
            )}
            <TextField size="small" fullWidth multiline minRows={2} label="Texto" value={body} onChange={set('body')} onFocus={() => { target.current = 'body'; }}
                error={isPush && body.length > limits.pushBodyMax} helperText={isPush ? `${body.length}/${limits.pushBodyMax}` : undefined} />
            <VariableChips content={value} onPick={insert} />
            <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Pré-visualização, com dados de exemplo</Typography>
                <ChannelPreview content={value} channel={channel} />
            </Box>
        </>
    );
};

/** O e-mail em blocos como o cliente de e-mail vai mostrar, com os dados de exemplo; o HTML é o do espelho do motor (ver emailPreview). */
const EmailPreview = ({ content }) => {
    const [view, setView] = useState('mobile');
    const preview = useMemo(() => emailPreview(content), [content]);
    if (!preview) return null;

    return (
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Pré-visualização, com dados de exemplo</Typography>
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, next) => next && setView(next)}>
                    <ToggleButton value="mobile">Celular</ToggleButton>
                    <ToggleButton value="desktop">Computador</ToggleButton>
                    <ToggleButton value="text">Texto puro</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Assunto:</b> {preview.subject || <em>(vazio)</em>}
                {preview.preheader && <Typography component="span" variant="body2" color="text.secondary"> · {preview.preheader}</Typography>}
            </Typography>
            {view === 'text' ? (
                <Box component="pre" sx={{ m: 0, p: 1.5, backgroundColor: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '10px', whiteSpace: 'pre-wrap', fontFamily: '"Roboto","Helvetica","Arial",sans-serif', fontSize: 14 }}>
                    {preview.text}
                </Box>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    {/* sandbox sem permissões: nem script nem link navegável saem do quadro, mesmo que o conteúdo traga algo estranho */}
                    <iframe title="Pré-visualização do e-mail" sandbox="" srcDoc={preview.html}
                        style={{ width: view === 'mobile' ? 375 : 640, maxWidth: '100%', height: 480, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }} />
                </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Aproximação do e-mail: o desenho exato, com o HTML que o motor gera, só aparece no envio de teste.
            </Typography>
        </Box>
    );
};

/** Os blocos do e-mail: assunto, pré-cabeçalho, título, parágrafos, botão e rodapé. O layout da marca e o descadastro não são editáveis. */
const EmailBlocksEditor = ({ value, onChange, limits }) => {
    const blocks = emailBlocks(value);
    const focus = useRef({ field: 'paragraph', index: 0 });
    const set = (patch) => onChange(withEmailBlocks(value, patch));
    const setParagraph = (index, text) => set({ paragraphs: blocks.paragraphs.map((p, i) => (i === index ? text : p)) });
    const setButton = (patch) => set({ button: { ...(blocks.button ?? { text: '', link: '' }), ...patch } });
    const onFocus = (field, index = 0) => () => { focus.current = { field, index }; };

    const insert = (token) => {
        const { field, index } = focus.current;
        if (field === 'paragraph') {
            if (blocks.paragraphs.length === 0) return set({ paragraphs: [token] });
            const at = Math.min(index, blocks.paragraphs.length - 1);
            return setParagraph(at, blocks.paragraphs[at] + token);
        }
        if (field === 'buttonText') return setButton({ text: (blocks.button?.text ?? '') + token });
        if (field === 'buttonLink') return setButton({ link: (blocks.button?.link ?? '') + token });
        return set({ [field]: blocks[field] + token });
    };
    const move = (index, delta) => {
        const to = index + delta;
        if (to < 0 || to >= blocks.paragraphs.length) return;
        const next = [...blocks.paragraphs];
        [next[index], next[to]] = [next[to], next[index]];
        set({ paragraphs: next });
    };

    const subjectOver = blocks.subject.length > limits.subjectSoft;
    return (
        <>
            <TextField size="small" fullWidth label="Assunto" value={blocks.subject} onChange={(e) => set({ subject: e.target.value })} onFocus={onFocus('subject')}
                color={subjectOver ? 'warning' : undefined} focused={subjectOver || undefined}
                helperText={`${blocks.subject.length}/${limits.subjectSoft} recomendados`} sx={{ mb: 1.5 }} />
            <TextField size="small" fullWidth label="Pré-cabeçalho (opcional)" value={blocks.preheader} onChange={(e) => set({ preheader: e.target.value })}
                onFocus={onFocus('preheader')} helperText="A linha cinza que aparece depois do assunto na caixa de entrada." sx={{ mb: 1.5 }} />
            <TextField size="small" fullWidth label="Título (opcional)" value={blocks.title} onChange={(e) => set({ title: e.target.value })}
                onFocus={onFocus('title')} sx={{ mb: 1.5 }} />

            <Typography variant="caption" color="text.secondary">Parágrafos</Typography>
            {blocks.paragraphs.map((paragraph, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.75 }}>
                    <TextField size="small" fullWidth multiline minRows={2} label={`Parágrafo ${index + 1}`} value={paragraph}
                        onChange={(e) => setParagraph(index, e.target.value)} onFocus={onFocus('paragraph', index)} />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Button size="small" disabled={index === 0} onClick={() => move(index, -1)} style={{ color: ORANGE }}>Subir</Button>
                        <Button size="small" disabled={index === blocks.paragraphs.length - 1} onClick={() => move(index, 1)} style={{ color: ORANGE }}>Descer</Button>
                        <Button size="small" color="error" onClick={() => set({ paragraphs: blocks.paragraphs.filter((_, i) => i !== index) })}>Remover</Button>
                    </Box>
                </Box>
            ))}
            <Button size="small" sx={{ mt: 0.5 }} style={{ color: ORANGE }} onClick={() => set({ paragraphs: [...blocks.paragraphs, ''] })}>Adicionar parágrafo</Button>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 1.5, mt: 1.5 }}>
                <TextField size="small" label="Texto do botão (opcional)" value={blocks.button?.text ?? ''} onChange={(e) => setButton({ text: e.target.value })}
                    onFocus={onFocus('buttonText')} helperText={`${(blocks.button?.text ?? '').length}/${limits.buttonSoft} recomendados`} />
                <TextField size="small" label="Link do botão" value={blocks.button?.link ?? ''} onChange={(e) => setButton({ link: e.target.value })}
                    onFocus={onFocus('buttonLink')} placeholder="{{Link}}" helperText="Começa com https://, ou use uma variável como {{Link}}." />
            </Box>
            <TextField size="small" fullWidth multiline minRows={2} label="Rodapé (opcional)" value={blocks.footer} onChange={(e) => set({ footer: e.target.value })}
                onFocus={onFocus('footer')} sx={{ mt: 1.5 }} />

            <VariableChips content={value} onPick={insert} />
            <Alert severity="info" sx={{ mt: 1.5 }}>
                Não se edita aqui: o cabeçalho com a marca, o link de descadastro (sempre presente quando a pessoa pode desligar o aviso) e o remetente.
                Valores que vêm de pessoas são sempre escritos como texto, nunca como código.
            </Alert>
            <EmailPreview content={value} />
        </>
    );
};

const EmailEditor = ({ value, onChange, limits }) => {
    // Tipo antigo: o e-mail sai de um modelo embutido no serviço. Dá para seguir assim ou passar para blocos (apaga o nome do modelo).
    if (!hasEmailBlocks(value)) {
        return (
            <>
                <TextField size="small" fullWidth label="Nome do modelo de e-mail" value={value.emailTemplate ?? ''}
                    helperText="Este tipo usa um modelo que já vem embutido no serviço de notificações (assunto, HTML e texto)."
                    onChange={(e) => onChange(withField(value, 'emailTemplate', e.target.value))} />
                <Button size="small" sx={{ mt: 1 }} style={{ color: ORANGE }} onClick={() => onChange(withEmailBlocksMode(value))}>
                    Escrever em blocos no lugar do modelo
                </Button>
            </>
        );
    }
    return (
        <>
            <EmailBlocksEditor value={value} onChange={onChange} limits={limits} />
            <Button size="small" color="inherit" sx={{ mt: 1, color: '#777' }} onClick={() => onChange(withEmailTemplateMode(value))}>
                Voltar a usar um modelo do serviço (descarta os blocos)
            </Button>
        </>
    );
};

const WhatsappEditor = ({ value, onChange, canEditConsent }) => {
    const template = whatsappTemplate(value);
    const patch = (fields) => onChange(withWhatsappTemplate(value, fields));
    const body = channelText(value, 'whatsapp').body;
    const variables = Object.entries(template.variables);
    const names = knownVariables(value);

    const setVariable = (oldPosition, position, field) => {
        const next = {};
        variables.forEach(([p, f]) => { if (p !== oldPosition) next[p] = f; });
        if (position !== '') next[position] = field;
        patch({ variables: next });
    };

    return (
        <>
            <Alert severity="info" sx={{ mb: 2 }}>
                Fora da janela de 24 horas, o WhatsApp só entrega modelo aprovado pela Meta. O texto abaixo é a cópia do que foi aprovado, para conferência: para
                mudar o corpo é preciso um novo modelo na Meta. O tipo só entra no ar com o modelo <b>aprovado</b>.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 2 }}>
                <TextField size="small" label="Nome do modelo na Meta" value={template.name} onChange={(e) => patch({ name: e.target.value })} />
                <TextField size="small" label="Idioma" value={template.language} onChange={(e) => patch({ language: e.target.value })} />
                <FormControl size="small">
                    <InputLabel>Categoria</InputLabel>
                    <Select label="Categoria" value={template.category} onChange={(e) => patch({ category: e.target.value })}>
                        {WHATSAPP_CATEGORIES.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" disabled={!canEditConsent}>
                    <InputLabel>Situação na Meta</InputLabel>
                    <Select label="Situação na Meta" value={template.status} onChange={(e) => patch({ status: e.target.value })}>
                        {WHATSAPP_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>
            {!canEditConsent && (
                <Typography variant="caption" color="text.secondary">A situação do modelo é mantida pelo editor de consentimento.</Typography>
            )}
            <TextField size="small" fullWidth multiline minRows={2} label="Texto aprovado (cópia)" sx={{ mt: 2 }} value={body}
                onChange={(e) => onChange(withChannelText(value, 'whatsapp', 'body', e.target.value))} />

            <Typography variant="body2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>Variáveis do modelo</Typography>
            {variables.length === 0 && <Typography variant="caption" color="text.secondary">Nenhuma. Ligue cada posição do modelo ({'{{1}}'}, {'{{2}}'}...) a um dado do tipo.</Typography>}
            {variables.map(([position, field]) => (
                <Box key={position} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField size="small" label="Posição" sx={{ width: 100 }} value={position} onChange={(e) => setVariable(position, e.target.value.trim(), field)} />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Dado</InputLabel>
                        <Select label="Dado" value={names.includes(field) ? field : ''} onChange={(e) => setVariable(position, position, e.target.value)}>
                            {names.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button size="small" color="error" onClick={() => setVariable(position, '', '')}>Remover</Button>
                </Box>
            ))}
            <Button size="small" style={{ color: ORANGE }} onClick={() => {
                const used = new Set(variables.map(([p]) => p));
                let n = 1;
                while (used.has(String(n))) n += 1;
                patch({ variables: { ...template.variables, [String(n)]: names[0] } });
            }}>Adicionar variável</Button>

            <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Pré-visualização, com dados de exemplo</Typography>
                <ChannelPreview content={value} channel="whatsapp" />
            </Box>
        </>
    );
};

const ChannelsTab = ({ value, onChange, canEditConsent, limits }) => {
    const enabled = enabledChannels(value);
    const lock = !canEditConsent;

    return (
        <>
            {lock && <Alert severity="info" sx={{ mb: 2 }}>Ligar ou desligar um canal muda quem é avisado e por onde: é do editor de consentimento. Os textos você edita.</Alert>}
            {enabled.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Ordem de preferência: {enabled.map((c, i) => `${i + 1}. ${availableChannels(value).find((a) => a.id === c)?.label ?? c}`).join('  ·  ')}
                </Typography>
            )}
            {availableChannels(value).map((channel) => {
                const on = enabled.includes(channel.id);
                const position = enabled.indexOf(channel.id);
                return (
                    <Section key={channel.id} sx={{ opacity: on ? 1 : 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: on ? 1.5 : 0 }}>
                            <FormControlLabel disabled={lock} label={<b>{channel.label}</b>}
                                control={<Switch checked={on} onChange={(e) => onChange(withChannel(value, channel.id, e.target.checked))} />} />
                            <Box sx={{ flexGrow: 1 }} />
                            {on && !lock && enabled.length > 1 && (
                                <>
                                    <Button size="small" disabled={position === 0} onClick={() => onChange(moveChannel(value, channel.id, -1))}>Subir</Button>
                                    <Button size="small" disabled={position === enabled.length - 1} onClick={() => onChange(moveChannel(value, channel.id, 1))}>Descer</Button>
                                </>
                            )}
                        </Box>
                        {on && channel.id === 'email' && <EmailEditor value={value} onChange={onChange} limits={limits} />}
                        {on && channel.id === 'whatsapp' && <WhatsappEditor value={value} onChange={onChange} canEditConsent={canEditConsent} />}
                        {on && (channel.id === 'push' || channel.id === 'app' || channel.id === 'sms') && (
                            <TextChannelEditor value={value} onChange={onChange} channel={channel.id} limits={limits} />
                        )}
                    </Section>
                );
            })}
        </>
    );
};

// ---- aba Dados ---------------------------------------------------------------------------------------------------------------

const DataTab = ({ value, onChange }) => {
    const fields = dataFields(value);
    const update = (index, patch) => onChange(withDataFields(value, fields.map((f, i) => (i === index ? { ...f, ...patch } : f))));

    return (
        <Section title="Dados que o produtor manda para este tipo">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Toda variável usada num texto precisa estar aqui (além das do sistema: {SYSTEM_VARIABLES.join(', ')}). Marque como sensível o que não deve aparecer na tela
                bloqueada. O exemplo só alimenta a pré-visualização.
            </Typography>
            {fields.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Nenhum dado além dos do sistema.</Typography>}
            {fields.map((f, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField size="small" label="Nome" sx={{ width: 180 }} value={f.name} onChange={(e) => update(i, { name: e.target.value })} />
                    <TextField size="small" label="Exemplo" sx={{ width: 220 }} value={f.example} onChange={(e) => update(i, { example: e.target.value })} />
                    <FormControlLabel label="Sensível" control={<Checkbox size="small" checked={f.sensitive} onChange={(e) => update(i, { sensitive: e.target.checked })} />} />
                    <IconButton size="small" aria-label="remover dado" onClick={() => onChange(withDataFields(value, fields.filter((_, j) => j !== i)))}>×</IconButton>
                </Box>
            ))}
            <Button size="small" style={{ color: ORANGE }} onClick={() => onChange(withDataFields(value, [...fields, { name: '', sensitive: false, example: '' }]))}>
                Adicionar dado
            </Button>
        </Section>
    );
};

// ---- aba Avançado (JSON) -----------------------------------------------------------------------------------------------------

/** O conteúdo cru. Edita-se aqui o que o formulário não cobre; um JSON inválido não sobrescreve o formulário. */
const JsonTab = ({ value, onChange }) => {
    const [text, setText] = useState(() => pretty(value));
    const [error, setError] = useState(null);
    const last = useRef(value);

    // O formulário mudou por fora desta aba: reflete o novo JSON (sem pisar no que está sendo digitado aqui).
    useEffect(() => {
        if (value !== last.current) { setText(pretty(value)); setError(null); last.current = value; }
    }, [value]);

    const edit = (e) => {
        setText(e.target.value);
        const parsed = parseContent(e.target.value);
        if (parsed.error) { setError(parsed.error); return; }
        setError(null);
        last.current = parsed.value;
        onChange(parsed.value);
    };

    return (
        <>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            <TextField multiline minRows={14} maxRows={28} fullWidth value={text} onChange={edit} inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13 } }} />
        </>
    );
};

// ---- grupo ---------------------------------------------------------------------------------------------------------------------

const GroupForm = ({ value, onChange }) => {
    const set = (field) => (e) => {
        const n = intOrNull(e.target.value);
        onChange(withField(value, field, Number.isNaN(n) ? e.target.value : n));
    };
    return (
        <Section title="Limite de mensagens por pessoa">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Os tipos que apontam para este grupo dividem o limite. Mudar o número vale para todos eles.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField size="small" label="Por dia" value={value.perDay ?? ''} onChange={set('perDay')} />
                <TextField size="small" label="Por semana" value={value.perWeek ?? ''} onChange={set('perWeek')} />
            </Box>
        </Section>
    );
};

// ---- o editor -----------------------------------------------------------------------------------------------------------------

/**
 * Editor do conteúdo de um tipo (abas Atributos, Canais, Dados e Avançado) ou de um grupo de limite. `value` é o objeto do conteúdo e `onChange`
 * recebe o objeto novo; chaves que o formulário não conhece são preservadas. A validação mostrada aqui espelha a do servidor, que decide de verdade.
 */
const TypeContentEditor = ({ kind, value, onChange, groups = null, canEditConsent = false, limits = DEFAULT_LIMITS }) => {
    const [tab, setTab] = useState(0);
    const { errors, warnings } = useMemo(() => validate(kind, value, groups, limits), [kind, value, groups, limits]);

    return (
        <Box>
            {errors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    <b>Falta ajustar antes de pedir a publicação:</b>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
                </Alert>
            )}
            {warnings.length > 0 && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                </Alert>
            )}
            {errors.length === 0 && warnings.length === 0 && <Alert severity="success" sx={{ mb: 1.5 }}>Tudo certo para pedir a publicação.</Alert>}

            {kind === 'group' ? (
                <>
                    <GroupForm value={value} onChange={onChange} />
                    <JsonTab value={value} onChange={onChange} />
                </>
            ) : (
                <>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} TabIndicatorProps={{ style: { backgroundColor: ORANGE } }}>
                        <Tab label="Atributos" /><Tab label="Canais" /><Tab label="Dados" /><Tab label="Avançado (JSON)" />
                    </Tabs>
                    {tab === 0 && <AttributesTab value={value} onChange={onChange} groups={groups} canEditConsent={canEditConsent} />}
                    {tab === 1 && <ChannelsTab value={value} onChange={onChange} canEditConsent={canEditConsent} limits={limits} />}
                    {tab === 2 && <DataTab value={value} onChange={onChange} />}
                    {tab === 3 && <JsonTab value={value} onChange={onChange} />}
                </>
            )}
        </Box>
    );
};

export default TypeContentEditor;
